/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ╢
║ victor.calle@iudigital.edu.co                              ╢
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Controlador para verificación de eventos de usuarios.
 *
 * Este controlador maneja la lógica de verificación de eventos asociados
 * a un usuario mediante su documento de identidad, incluyendo redirección
 * automática cuando solo existe un evento.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date 2025-10-22
 * @updated 2025-10-22
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import VerifyEventsByDocument from "../modules/VerifyEventsByDocument";
import { getAllEventsPerDocument } from "../services/VerifyEventsService";
import MDAlert from "components/Alert";

/**
 * Componente controlador principal para verificación de eventos por documento.
 * Maneja la búsqueda de eventos y redirección automática cuando solo hay un resultado.
 *
 * @returns {JSX.Element} Componente de React con la interfaz de verificación de eventos.
 */
export default function VerifyEventsController() {
  const [document, setDocument] = useState({});
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAlert, setIsAlert] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();

  /**
   * Ejecuta la búsqueda de eventos asociados al documento ingresado.
   * Si solo encuentra un evento, redirige automáticamente a la vista de detalle.
   * Si encuentra múltiples eventos, muestra el listado.
   *
   * @param {Event} e - Evento del formulario (opcional).
   */
  const handleSubmitRequest = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!document.name || document.name.trim() === "") {
      handleAlert("fail", "El número de documento no puede estar vacío.");
      return;
    }

    setIsLoading(true);
    try {
      const result = await getAllEventsPerDocument(document.name);

      if (result.status !== false) {
        const events = result.payload;

        // Si solo hay un evento, redirigir automáticamente
        if (events && events.length === 1) {
          navigate(`/verify/${events[0].id}/false`);
          return;
        }

        // Si hay múltiples eventos, mostrar el listado
        await setData(events);
        handleAlert("success");
      } else {
        handleAlert("fail", result.message || "Error al realizar la consulta");
      }
    } catch (error) {
      handleAlert("fail", error.message || "Error al realizar la consulta");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja los cambios en los campos del formulario.
   *
   * @param {string} fieldName - Nombre del campo que cambió.
   * @param {any} value - Nuevo valor del campo.
   */
  const handleFieldChange = (fieldName, value) => {
    setDocument({
      ...document,
      [fieldName]: value,
    });
  };

  /**
   * Maneja la visualización de alertas en la interfaz.
   *
   * @param {string} typeAlert - Tipo de alerta ("success" o "fail").
   * @param {string} message - Mensaje a mostrar en la alerta.
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  return (
    <>
      <VerifyEventsByDocument
        handleFieldChange={handleFieldChange}
        handleSubmit={handleSubmitRequest}
        data={data}
        isLoading={isLoading}
      />
      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />
    </>
  );
}
